import { format, isBefore, type NormalizedInterval } from 'date-fns'

/* prettier-ignore */
// courtesy of stackoverflow
// https://stackoverflow.com/a/63080026
type Combine<A, B> =
    Omit<A, keyof B> // items in A that aren't in B
  & Omit<B, keyof A> // items in B that aren't in A
  & { [K in keyof A & keyof B]: A[K] | B[K] }; // union of both.
/* prettier-ignore-end */

/**
 * Fills missing month objects in an array of month objects. Takes a period to
 * fill from and to, a template for what an empty month object looks like, and
 * optionally a format for the month values.
 *
 * It strips months that don't land inside the period.
 *
 * The period bounds are inclusive.
 *
 * monthData would look like this:
 * [
 *   { month: '2012-05', count: 2 },
 *   { month: '2012-07', count: 10 },
 * ]
 *
 * A template would look like { count: null, other: 2 }
 *
 * Result:
 * [
 *   { month: '2012-05', count: 2 },
 *   { month: '2012-06', count: null },
 *   { month: '2012-07', count: 10 },
 * ]
 */
export default function fillMonths<M extends { month: string }, T extends Record<string, unknown>>(
  monthData: M[],
  { start, end }: NormalizedInterval,
  template: T,
): Combine<T, M>[] {
  const formatString = 'yyyy-MM'
  const startDate = new Date(start)
  const endDate = new Date(end)

  const months: Combine<T, M>[] = []
  // While the start date month is before or equal to the end date month
  while (isBefore(startDate, endDate) || startDate.getMonth() === endDate.getMonth()) {
    const formattedMonth = format(startDate, formatString)
    const currentMonthData = monthData.find((monthObject) => monthObject.month === formattedMonth)

    months.push(<Combine<T, M>>{ ...template, ...currentMonthData, month: formattedMonth })

    startDate.setMonth(startDate.getMonth() + 1) // going past december will rollover to the next year.
  }

  return months
}
